// apps/web/src/pages/author/widget-config/ranked-tier-config.tsx
//
// Phase 8 config sub-form for template `ranked-tier` — drives the inline
// configSchema editor per authr-05. Field set mirrors
// packages/shared/src/schemas/widget-templates/ranked-tier.ts AND the
// per-template walker at packages/shared/src/forms/per-template/ranked-tier.ts
// (Plan 03).
//
// Sub-form fields use per-path useWatch only — NEVER top-level (RESEARCH
// §"Pattern 3"). No formState reads. Every useFieldArray map uses field.id
// keys per authr-03 (Plan 01 grep gate enforces).
//
// configSchema shape (verbatim):
//   optionSetWidgetId: string
//   tiers: ReadonlyArray<{ id; label; rank; colors: { bg; border; fg } }>  (1..12)
//
// Note on field naming: the SHARED schema uses `optionSetWidgetId` (capital S).
// We mirror it here exactly per Phase 7 ranked-tier.ts line 70.
//
// W11 — exports `defaultRankedTierConfig(): RankedTierConfig` factory.

import type { RankedTierConfig } from "@the-recipe-spread/shared";
import { useFieldArray, useFormContext } from "react-hook-form";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FieldArrayRow } from "../FieldArrayRow";

export interface RankedTierConfigSubFormProps {
  instanceId: string;
  basePath: string;
}

// W11 — minimum-valid RankedTierConfig per the configSchema:
//   tiers.min(1); colors.{bg,border,fg} non-empty; rank positive int <= 12.
//   Use plain hex colors that satisfy the CSS_COLOR_RE positive-list.
export function defaultRankedTierConfig(): RankedTierConfig {
  return {
    optionSetWidgetId: "",
    tiers: [
      {
        id: "tier-1",
        label: "Tier 1",
        rank: 1,
        colors: { bg: "#ffffff", border: "#000000", fg: "#000000" },
      },
    ],
  };
}

export function RankedTierConfigSubForm({
  instanceId: _instanceId,
  basePath,
}: RankedTierConfigSubFormProps): React.ReactElement {
  const { control } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `${basePath}.tiers`,
  });

  return (
    <div className="flex flex-col gap-4">
      <FormField
        control={control}
        name={`${basePath}.optionSetWidgetId`}
        render={({ field: f }) => (
          <FormItem data-field-path={`${basePath}.optionSetWidgetId`}>
            <FormLabel>Source option-set widget id</FormLabel>
            <FormControl>
              <Input {...f} value={f.value ?? ""} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <Fieldset legend="TIERS">
        {fields.length === 0 ? (
          <EmptyState
            title="No tiers yet."
            cta={{
              label: "Add tier",
              onClick: () =>
                append({
                  id: `tier-${fields.length + 1}`,
                  label: `Tier ${fields.length + 1}`,
                  rank: fields.length + 1,
                  colors: { bg: "#ffffff", border: "#000000", fg: "#000000" },
                }),
            }}
          />
        ) : (
          <>
            {fields.map((field, index) => (
              <FieldArrayRow
                key={field.id}
                singular="tier"
                index={index}
                canMoveUp={index > 0}
                canMoveDown={index < fields.length - 1}
                onMoveUp={() => move(index, index - 1)}
                onMoveDown={() => move(index, index + 1)}
                onRemove={() => remove(index)}
                deleteDialogTitle="Delete tier?"
                deleteDialogBody="This tier will be removed from the ranking."
                deleteConfirmLabel="Delete tier"
              >
                <FormField
                  control={control}
                  name={`${basePath}.tiers.${index}.id`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.tiers.${index}.id`}>
                      <FormLabel>Id (kebab-case)</FormLabel>
                      <FormControl>
                        <Input {...f} value={f.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.tiers.${index}.label`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.tiers.${index}.label`}>
                      <FormLabel>Label</FormLabel>
                      <FormControl>
                        <Input {...f} value={f.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.tiers.${index}.rank`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.tiers.${index}.rank`}>
                      <FormLabel>Rank (1-12)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          max="12"
                          value={f.value ?? ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            f.onChange(raw === "" ? undefined : Number(raw));
                          }}
                          onBlur={f.onBlur}
                          name={f.name}
                          ref={f.ref}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.tiers.${index}.colors.bg`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.tiers.${index}.colors.bg`}>
                      <FormLabel>Background color</FormLabel>
                      <FormControl>
                        <Input {...f} value={f.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.tiers.${index}.colors.border`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.tiers.${index}.colors.border`}>
                      <FormLabel>Border color</FormLabel>
                      <FormControl>
                        <Input {...f} value={f.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.tiers.${index}.colors.fg`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.tiers.${index}.colors.fg`}>
                      <FormLabel>Foreground color</FormLabel>
                      <FormControl>
                        <Input {...f} value={f.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FieldArrayRow>
            ))}
            <button
              type="button"
              onClick={() =>
                append({
                  id: `tier-${fields.length + 1}`,
                  label: `Tier ${fields.length + 1}`,
                  rank: fields.length + 1,
                  colors: { bg: "#ffffff", border: "#000000", fg: "#000000" },
                })
              }
              className="self-start text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
            >
              Add tier
            </button>
          </>
        )}
      </Fieldset>
    </div>
  );
}
RankedTierConfigSubForm.displayName = "RankedTierConfigSubForm";
